import React, { useState, useEffect } from "react";
import { doc, updateDoc, arrayUnion, getDocs, collection, addDoc, getDoc } from "firebase/firestore";
import { db, auth } from "./firebase";
import { getActiveWishlistCount } from "./utils";
import { useTranslation } from 'react-i18next';
// Firebase Functions will be imported dynamically when needed
import { loadStripe } from "@stripe/stripe-js";
import Modal from './Modal';
import PremiumModal from './PremiumModal';
import { isValidProductUrl, isBannedWordExact } from "./utils/affiliateUtils";

import { prepareImageBlob, moderateImageBlob } from './utils/imagePipeline';
import WishlistSpaceLimitModal from './WishlistSpaceLimitModal'; // Import the new modal
import { secureImageUrl } from './utils/secureImageUrl';
import { cacheImageToFirebase } from './utils/cacheImage';
import { normalizeImageUrl } from './utils/normalizeImageUrl';
import { isInvalidProductTitle, processProductUrl } from './utils/productAddition';

export default function AggiungiProdotto({ onClose, wishlistId: preselectedWishlistId }) {
  console.log('🏗️ AggiungiProdotto component rendered');
  const { t } = useTranslation();
  const [wishlists, setWishlists] = useState([]);
  const [wishlistId, setWishlistId] = useState(preselectedWishlistId || "");
  const [wishlistLimitReached, setWishlistLimitReached] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [step, setStep] = useState(preselectedWishlistId ? "insertLink" : "selectWishlist");
  const [showSpaceLimitModal, setShowSpaceLimitModal] = useState(false);
  const [allowAddWithoutImage, setAllowAddWithoutImage] = useState(false);

  const [link, setLink] = useState("");
  const [title, setTitle] = useState("");
  const [image, setImage] = useState("");
  const [imageFile, setImageFile] = useState(null); // Stato per il file immagine caricato
  const [manualMode, setManualMode] = useState(false);
  const [autoParseSuccess, setAutoParseSuccess] = useState(false); // Tracks if processProductUrl succeeded

  const [error, setError] = useState("");
  const [imageError, setImageError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false); // Added saving state

  // Firebase error mapping function
  function mapFirebaseErrorToKey(code) {
    switch (code) {
      case 'permission-denied':
      case 'unauthenticated':
        return 'saveErrorInsufficientPermissions';
      case 'unavailable':
        return 'networkError';
      case 'invalid-argument':
        return 'formValidationFailed';
      default:
        return 'saveErrorGeneric';
    }
  }

  // Safe translation function with fallback
  function tSafe(keyOrText) {
    if (!keyOrText || typeof keyOrText !== 'string') return '';
    const s = String(keyOrText);
    // If it contains whitespace, it's likely already human text
    if (/\s/.test(s)) return s;
    // Otherwise, try translate key
    return t(s);
  }

    // Utility to check if a wishlist is at its product limit
    const isWishlistAtLimit = (products) => {
      return products.length >= 10; // Assuming 10 is the limit
    };


    // Funzione per gestire il checkout Stripe
    const handleUpgradeToPremium = async () => {
      if (!auth.currentUser) {
        console.error("Utente non autenticato");
        return;
      }

      try {
        console.log("🔄 Inizializzazione checkout Stripe...");

        // Inizializza le Firebase Functions
        const functions = (await import("firebase/functions")).getFunctions();
        const createCheckoutSession = (await import("firebase/functions")).httpsCallable(functions, 'createCheckoutSession');

        // Chiama la Cloud Function per creare la sessione
        const result = await createCheckoutSession({
          userId: auth.currentUser.uid
        });

        console.log("✅ Sessione Stripe creata:", result.data);

        // Inizializza Stripe
        const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);
        if (!stripe) {
          throw new Error(t('auth.warnings.stripeInitError'));
        }

        // Reindirizza al checkout
        const { error } = await stripe.redirectToCheckout({
          sessionId: result.data.sessionId
        });

        if (error) {
          console.error("❌ Errore nel reindirizzamento:", error);
          alert(t('auth.errors.paymentRedirectError'));
        }

      } catch (error) {
        console.error("❌ Errore nell'upgrade a Premium:", error);
        alert(t('auth.errors.paymentSessionError'));
      }
    };

  useEffect(() => {
    const checkLimit = async () => {
      const count = await getActiveWishlistCount(auth.currentUser?.uid);
      if (count >= 10) {
        setWishlistLimitReached(true);
        setShowLimitModal(true);
      }
    };
    if (!preselectedWishlistId) checkLimit();
  }, [preselectedWishlistId]);

  useEffect(() => {
    const fetchWishlists = async () => {
      const snap = await getDocs(collection(db, "wishlists"));
      const filtered = snap.docs
        .filter(d => d.data().userId === auth.currentUser?.uid && !d.data().archived)
        .map(d => ({ id: d.id, ...d.data() }));
      setWishlists(filtered);
    };
    if (!preselectedWishlistId) fetchWishlists();
  }, [preselectedWishlistId]);



  // Function to process external images from websites with fallback mechanisms
  const processExternalImage = async (imageUrl) => {
    console.log("🌐 Starting external image processing for:", imageUrl);
    
    try {
      // Cache image to Firebase Storage with fallback to original URL
      console.log("📦 Caching external image to Firebase Storage...");
      const productId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const cacheResult = await cacheImageToFirebase(imageUrl, productId);
      
      if (cacheResult.cached) {
        console.log("✅ External image cached successfully to Firebase Storage");
        setImage(cacheResult.url);
      } else {
        // Fallback: use original URL or cached fallback URL
        console.warn("⚠️ Image caching failed, using original URL as fallback");
        const fallbackUrl = cacheResult.url || imageUrl;
        setImage(fallbackUrl);
      }
    } catch (cachingError) {
      console.error("❌ Image caching error, using original URL:", cachingError);
      // Fallback to original image URL instead of showing error
      setImage(imageUrl);
    }
  };

  const handleParseLink = async () => {
    const url = link?.trim();
    console.log('🚀 PARSE LINK: Starting with centralized utility for URL:', url);
    if (!url) {
      console.log('❌ PARSE LINK: No URL provided');
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Use centralized product URL processing
      const result = await processProductUrl(url);
      
      // Handle validation/processing errors
      if (!result.success) {
        if (result.needsManualMode) {
          console.log('⚠️ Auto-load failed, switching to manual mode');
          setError(t(result.errorKey) || t('errors.autoLoadError'));
          setManualMode(true);
          return;
        }
        
        // URL validation or moderation failed
        setError(t(result.errorKey) || t('modals.bannedContent'));
        return;
      }

      // Success - set title and image
      setTitle(result.title);
      setImage(result.image || "");
      setAutoParseSuccess(true); // Mark as auto-parsed successfully
      
      // Store the affiliate URL for later use
      if (result.affiliateUrl) {
        setLink(result.affiliateUrl);
      }
      
      setStep("review");
      console.log('✅ PARSE LINK: Success via centralized utility');
    } catch (error) {
      console.error("❌ PARSE LINK: Error occurred:", error);
      setError(t('errors.autoLoadError'));
      setManualMode(true);
    } finally {
      console.log('🏁 PARSE LINK: Completed, setting loading to false');
      setLoading(false);
    }
  };

  const handleUploadImage = async (file) => {
    setImageError("");

    if (!file) {
      setImageError(t('addProductModal.selectImage'));
      return;
    }

    // Check file size before processing (100MB max - we'll compress it down)
    if (file.size > 100 * 1024 * 1024) {
      setImageError(t('errors.imageTooBig'));
      return;
    }

    try {
      console.log(`🗜️ Processing image with ultra compression: ${file.name}, ${Math.round(file.size / 1024)}KB`);

      // Use unified image pipeline with ultra-aggressive compression
      const prepared = await prepareImageBlob(file);
      console.log(`✅ Ultra-compressed to: ${Math.round(prepared.blob.size / 1024)}KB (${((1 - prepared.blob.size / file.size) * 100).toFixed(1)}% reduction)`);

      // Moderate the processed image
      const isAllowed = await moderateImageBlob(prepared.blob);
      if (!isAllowed) {
        setImageError(t('errors.imageNotAllowed'));
        setImage("");
        return;
      }

      // Convert to base64 for display
      const arrayBuffer = await prepared.blob.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
      const dataUrl = `data:${prepared.mime};base64,${base64}`;

      setImage(dataUrl);
      setImageError("");
      console.log("✅ Ultra-compressed image ready for storage");

    } catch (error) {
      console.error("❌ Error processing image:", error);
      if (error === 'invalid_mime') {
        setImageError(t('errors.invalidFileType'));
      } else {
        setImageError(t('errors.imageProcessingError'));
      }
    }
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);

    try {
      // Validate required fields - only title is required
      if (!wishlistId) {
        setError(t('addProductModal.chooseWishlist'));
        return;
      }

      if (!title.trim()) {
        setError(t('errors.titleRequired'));
        return;
      }

      // Auth preconditions (same as Home)
      if (!auth.currentUser?.uid) {
        setError(t('errors.mustBeLoggedIn'));
        return;
      }

      // Skip client-side banned words check for products - let server-side moderation handle it

      // Check storage limit before proceeding
      const selectedWishlist = wishlists.find(w => w.id === wishlistId);
      if (selectedWishlist && isWishlistAtLimit(selectedWishlist.products || []) && image && !allowAddWithoutImage) {
        setShowSpaceLimitModal(true);
        return;
      }

      // Skip re-validation if processProductUrl already succeeded
      // This prevents duplicate moderation calls that could fail on retry
      if (!autoParseSuccess) {
        // Only validate/moderate for manual mode entries
        console.log("🔍 Manual mode: running validation and moderation...");
        
        // Verifica URL se presente (opzionale in modalità manuale)
        if (link && link.trim()) {
          const { valid, reasonKey } = isValidProductUrl(link);
          if (!valid) {
            setError(t(reasonKey) || t('modals.bannedContent'));
            return;
          }
        }

        // Unified moderation check for manual entries
        try {
          const { moderateText } = await import('./utils/contentModeration.js');
          
          const textResult = await moderateText(title.trim(), "product");
          if (!textResult.allowed) {
            console.warn("⚠️ Text blocked in moderation:", { title: title.substring(0, 50), reason: textResult.reason });
            setError(t('modals.bannedContent') || 'Content not allowed');
            return;
          }
          
          console.log("✅ Manual entry moderation passed");
        } catch (moderationError) {
          console.warn("⚠️ Moderation error, content blocked for safety:", moderationError);
          setError(t('errors.moderationError') || 'Moderation error');
          return;
        }
      } else {
        console.log("✅ Auto-parse succeeded, skipping re-validation (already moderated by processProductUrl)");
      }

      // Cache image to Firebase Storage if it's an external URL
      let finalImageUrl = image || null;
      
      console.log("🔍 Image check before save:", {
        hasImage: !!image,
        imageType: image ? (image.startsWith('data:') ? 'base64' : image.startsWith('http') ? 'url' : 'other') : 'none',
        isFirebaseStorage: image?.includes('firebasestorage.googleapis.com'),
        imagePreview: image?.substring(0, 100)
      });
      
      if (image && !image.startsWith('data:') && !image.includes('firebasestorage.googleapis.com')) {
        console.log("📦 Image is external URL, caching to Firebase Storage...");
        try {
          const productId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const cacheResult = await cacheImageToFirebase(image, productId);
          
          console.log("📦 Caching result:", cacheResult);
          
          if (cacheResult.cached) {
            console.log("✅ Image cached successfully to Firebase Storage:", cacheResult.url.substring(0, 80));
            finalImageUrl = cacheResult.url;
          } else {
            // Fallback: use original URL instead of blocking
            console.warn("⚠️ Image caching failed, using original URL:", cacheResult.error);
            finalImageUrl = cacheResult.url || image;
          }
        } catch (cacheError) {
          // Fallback: use original URL instead of blocking
          console.error("❌ Image caching exception, using original URL:", cacheError);
          finalImageUrl = image;
        }
      } else if (image && image.startsWith('http://')) {
        // Convert HTTP to HTTPS and continue
        console.warn("⚠️ HTTP-only image detected, converting to HTTPS");
        finalImageUrl = image.replace('http://', 'https://');
      } else if (image && image.includes('firebasestorage.googleapis.com')) {
        console.log("✅ Image already cached in Firebase Storage, using existing URL");
      } else if (image && image.startsWith('data:')) {
        console.log("✅ Image is base64, will be saved as-is");
      }

      // Create product object - exactly like Home.jsx
      const product = {
        title: title.trim(),
        image: finalImageUrl,
        url: (link && link.trim()) ? link.trim() : "",
        reservedBy: null
      };

      console.log("💾 Saving product to Firebase:", {
        wishlistId,
        product: { ...product, image: product.image ? '[IMAGE_DATA]' : null }
      });

      // Save to Firebase - prepend to beginning (newest first)
      const wishlistRef = doc(db, "wishlists", wishlistId);
      const wishlistDoc = await getDoc(wishlistRef);
      const currentProducts = wishlistDoc.exists() ? (wishlistDoc.data().products || []) : [];
      const updatedProducts = [product, ...currentProducts];
      
      await updateDoc(wishlistRef, {
        products: updatedProducts
      });

      console.log("✅ Prodotto aggiunto con successo");

      // Reset form and close
      setTitle("");
      setLink("");
      setImage("");
      setImageFile(null);
      setManualMode(false);
      setAutoParseSuccess(false);
      setStep(preselectedWishlistId ? "insertLink" : "selectWishlist");
      setError("");
      onClose();

    } catch (error) {
      console.error('❌ Errore aggiunta prodotto:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        wishlistId,
        hasImage: !!image,
        imageSize: image ? image.length : 0
      });

      // More specific error handling
      if (error.code === 'invalid-argument') {
        setError('Errore nei dati del prodotto. Riprova.');
      } else if (error.code === 'permission-denied') {
        setError('Permessi insufficienti. Verifica di essere autenticato.');
      } else if (error.message?.includes('maximum size')) {
        setError(t('errors.imageTooBig'));
      } else {
        setError('Errore nel salvataggio. Riprova.');
      }
    } finally {
      setSaving(false);
    }
  };


  return (
    <>
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0,0,0,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
      overflow: "hidden"
    }}>
      <div style={{
        background: "#fff",
        padding: "20px",
        borderRadius: "16px",
        width: "90%",
        maxWidth: "400px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
        textAlign: "center",
        maxHeight: "90vh",
        overflowY: "auto",
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)"
      }}>
        <h2 style={styles.title}>
          {step === "selectWishlist" ? t('addProductModal.chooseWishlist') :
            manualMode ? t('addProductModal.insertManually') :
            t('addProductModal.addProduct')}
        </h2>

        {step === "selectWishlist" && (
          <>
            {wishlists.map(w => (
              <button
                key={w.id}
                style={styles.selectBtn}
                onClick={() => {
                  setWishlistId(w.id);
                  setStep("insertLink");
                }}
              >
                {w.name}
              </button>
            ))}
            <button style={styles.greyBtn} onClick={onClose}>{t('common.cancel')}</button>
          </>
        )}

        {step === "insertLink" && (
          <>
            <input
              style={styles.input}
              placeholder={t('addProductModal.insertLink')}
              value={link}
              onChange={(e) => {
                console.log('📝 INPUT CHANGED:', e.target.value);
                setLink(e.target.value);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  console.log('⌨️ ENTER PRESSED!');
                  e.preventDefault();
                  handleParseLink();
                }
              }}
            />
            {error && <p style={styles.error}>{error}</p>}
            <button 
              style={styles.blackBtn} 
              onClick={() => {
                console.log('🎯 CONTINUE BUTTON CLICKED!');
                console.log('🎯 Current link:', link);
                console.log('🎯 Loading state:', loading);
                if (!loading) {
                  console.log('🚀 Calling handleParseLink...');
                  handleParseLink();
                } else {
                  console.log('⏳ Already loading, skipping click');
                }
              }} 
              disabled={loading}
            >
              {loading ? t('common.loading') : t('addProductModal.continue')}
            </button>
            <button style={{...styles.greyBtn, marginTop: "12px"}} onClick={() => {
              setManualMode(true);
              setStep("review");
            }}>{t('addProductModal.insertManually')}</button>
          </>
        )}

        {(manualMode || step === "review") && (
          <>
            <input
              style={{...styles.input, marginTop: "12px"}}
              placeholder={t('addProductModal.productTitle')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            {manualMode && (
              <>
                <input
                  style={styles.input}
                  placeholder={t('addProductModal.insertLink')}
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                />
                <input
                  style={styles.input}
                  placeholder={t('addProductModal.imageUrl')}
                  value={image}
                  onChange={(e) => setImage(e.target.value)}
                />
              </>
            )}
            <label style={{ fontSize: "14px", color: "#666", marginBottom: "8px", display: "block" }}>
              {t('addProductModal.uploadImage')}
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                setImageFile(file);
                handleUploadImage(file);
              }}
              style={{ marginBottom: "16px" }}
            />
            {image && (
              <div style={{ marginBottom: "16px", textAlign: "center" }}>
                <img
                  src={image}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "120px",
                    borderRadius: "8px",
                    objectFit: "cover"
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    setImageError(t('addProductModal.imageLoadError'));
                  }}
                  onLoad={(e) => {
                    e.target.style.display = 'block';
                    if (imageError === t('addProductModal.imageLoadError')) {
                      setImageError("");
                    }
                  }}
                />
              </div>
            )}
            {imageError && <p style={styles.error}>{imageError}</p>}
            <p style={styles.disclaimer}>
              {t('addProductModal.disclaimer')}
            </p>
            {error && (
              <p style={{ color: '#D32F2F', marginTop: 8, marginBottom: 8, fontSize: "14px" }}>
                {tSafe(error)}
              </p>
            )}
            <div style={styles.btnGroup}>
              <button
                style={styles.blackBtn}
                onClick={handleSave} // Changed to handleSave
                disabled={saving} // Changed to disabled={saving}
              >
                {saving ? t('common.saving') : t('common.add')}
              </button>
              <button style={styles.greyBtn} onClick={onClose}>{t('common.cancel')}</button>
            </div>
          </>
        )}
      </div>
    </div>
      <PremiumModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        onUpgrade={handleUpgradeToPremium}
      />

      <WishlistSpaceLimitModal
        isOpen={showSpaceLimitModal}
        onClose={() => {
          setShowSpaceLimitModal(false);
          setAllowAddWithoutImage(false);
        }}
        onContinueWithoutImages={() => {
          setAllowAddWithoutImage(true);
          setShowSpaceLimitModal(false);
          // Continue with the save operation without image
          const productWithoutImage = {
            title: title.trim(),
            image: null,
            url: (link && link.trim()) ? link.trim() : "",
            reservedBy: null
          };

          // Get current products and prepend new item (newest first)
          getDoc(doc(db, "wishlists", wishlistId)).then(wishlistDoc => {
            const currentProducts = wishlistDoc.exists() ? (wishlistDoc.data().products || []) : [];
            const updatedProducts = [productWithoutImage, ...currentProducts];
            
            return updateDoc(doc(db, "wishlists", wishlistId), {
              products: updatedProducts
            });
          }).then(() => {
            console.log("✅ Prodotto aggiunto senza immagine");
            setTitle("");
            setLink("");
            setImage(null);
            setImageFile(null);
            setManualMode(false);
            setAutoParseSuccess(false);
            setStep("selectWishlist");
            setError("");
            onClose();
          }).catch((error) => {
            console.error('❌ Errore aggiunta prodotto senza immagine:', error);
            setError(t('errors.saveErrorGeneric'));
          });
        }}
        onManageProducts={() => {
          setShowSpaceLimitModal(false);
          setAllowAddWithoutImage(false);
          onClose();
        }}
        onCreateNewWishlist={() => {
          setShowSpaceLimitModal(false);
          setAllowAddWithoutImage(false);
          onClose();
        }}
      />
    </>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    paddingTop: "40px",
    overflowY: "auto",
    zIndex: 9999,
    fontFamily: "Poppins, sans-serif",
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "32px",
    width: "90%",
    maxWidth: "320px",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  title: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "8px",
    textAlign: "center",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    fontSize: "15px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
    marginBottom: "4px",
  },
  blackBtn: {
    backgroundColor: "#000",
    color: "#fff",
    padding: "14px",
    borderRadius: "8px",
    border: "none",
    fontWeight: 600,
    fontSize: "15px",
    width: "100%",
    cursor: "pointer",
    marginTop: "8px",
  },
  greyBtn: {
    backgroundColor: "#eee",
    color: "#000",
    padding: "14px",
    borderRadius: "8px",
    border: "none",
    fontWeight: 500,
    fontSize: "15px",
    width: "100%",
    cursor: "pointer",
    marginTop: "8px",
  },
  btnGroup: {
    display: "flex",
    flexDirection: "row",
    gap: "12px",
    marginTop: "16px",
  },
  selectBtn: {
    width: "100%",
    padding: "14px",
    borderRadius: "8px",
    border: "1px solid #000",
    fontWeight: "600",
    fontSize: "15px",
    backgroundColor: "#fff",
    cursor: "pointer",
    marginBottom: "8px",
  },
  error: {
    color: "#d00",
    fontSize: "14px",
    textAlign: "left",
    marginTop: "4px",
    marginBottom: "8px",
  },
  preview: {
    width: "100%",
    borderRadius: "10px",
    marginTop: "12px",
    marginBottom: "16px",
  },
  disclaimer: {
    fontSize: "12px",
    color: "#666",
    lineHeight: "1.4",
    marginBottom: "16px",
  },
};